import { useId, useRef, type ChangeEvent } from 'react'
import { Upload, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'

/**
 * FileInput — поле выбора ОДНОГО файла в форме (выбор сейчас, отправка — кнопкой формы). Заменяет
 * нестилизованный системный `<input type="file">` («Выберите файл»): кнопка «Выбрать файл», имя
 * выбранного файла или «Файл не выбран», кнопка «Убрать файл». Оформление — как у `Input`:
 * те же граница, радиус, фон и состояния invalid/disabled из токенов.
 *
 * Компонент УПРАВЛЯЕМЫЙ: выбранный файл задаёт проп `value`, сброс формы (`value = null`) сразу
 * возвращает поле в «Файл не выбран». Системное поле скрыто и после каждого выбора очищается —
 * повторный выбор того же файла снова вызывает `onChange`.
 *
 * Поле совместимо с render-пропом `Field`: `id` и `aria-*` уходят на скрытое системное поле
 * (метка открывает выбор файла, как у `SingleDocumentUpload`). Кнопки внутри — `type="button"`,
 * чтобы не отправлять форму, в которой стоит поле.
 *
 * Для загрузки документа сразу после выбора (с состояниями «загружен/заменить/удалить») —
 * `SingleDocumentUpload`, не этот компонент.
 */
export interface FileInputProps {
  /** Выбранный файл; `null` — «Файл не выбран». */
  value: File | null
  /** Выбор файла (`File`) или его снятие кнопкой «Убрать файл» (`null`). */
  onChange: (file: File | null) => void
  /** Допустимые форматы для системного окна выбора файла (атрибут `accept`). */
  accept?: string
  disabled?: boolean
  invalid?: boolean
  /** Подпись кнопки выбора. */
  buttonLabel?: string
  /** Текст при невыбранном файле. */
  placeholder?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  'aria-required'?: boolean
  className?: string
}

export function FileInput({
  value,
  onChange,
  accept,
  disabled = false,
  invalid,
  buttonLabel = 'Выбрать файл',
  placeholder = 'Файл не выбран',
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
  className,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const nameId = useId()
  const isInvalid = ariaInvalid ?? invalid

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    // Сброс значения — чтобы повторный выбор того же файла снова вызвал onChange.
    e.target.value = ''
    if (selected) onChange(selected)
  }

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-8 border py-0.5 pl-0.5 pr-2',
        // Недоступное поле: приглушённый фон, как у Input; кнопка гасится своим disabled-стилем.
        disabled ? 'cursor-not-allowed bg-subtle' : 'bg-white',
        isInvalid ? 'border-red' : 'border-line-strong',
        className,
      )}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        aria-label={ariaLabel}
        aria-invalid={isInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        aria-required={ariaRequired}
        className="hidden"
        disabled={disabled}
        onChange={handleChange}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={disabled}
        aria-describedby={nameId}
        onClick={() => inputRef.current?.click()}
        leftIcon={<Upload className="size-3.5 stroke-current" aria-hidden />}
      >
        {buttonLabel}
      </Button>
      {/* Длинное имя обрезается многоточием; полное — во всплывающей подсказке браузера. Нулевая
          базовая ширина (w-0 + flex-1): имя занимает только свободное место и не раздвигает
          контейнер, который считает ширину по содержимому (например, ячейку таблицы). */}
      <span
        id={nameId}
        title={value?.name}
        className={cn('w-0 flex-1 truncate text-14', value ? 'text-txt' : 'text-txt-tertiary')}
      >
        {value ? value.name : placeholder}
      </span>
      {value && !disabled ? (
        <button
          type="button"
          aria-label="Убрать файл"
          title="Убрать файл"
          className="shrink-0 rounded-8 p-1 text-txt-secondary transition-colors hover:bg-hover hover:text-txt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => onChange(null)}
        >
          <X className="size-4 stroke-current" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
